import React from "react";
import { Link } from "react-router-dom";
import { Module, Cluster, Text, Stack } from "@thecb/components";
import { toFormattedDate } from "@/util/dates";
import {
  resolveLineItemDescriptions,
  displayCurrency,
  toSentenceCase,
} from "@/util/common";
import { TableBody, Table } from "@/components/generic/Table";
import { RightCarrot, DownCarrot } from "./Icons";
import {
  RefundTableHeaderRow,
  RefundTableRow,
  LineItemTableRow,
  StatusColumn,
  LineItemDescription,
  LineItemSubDescription,
  LineItemAmount,
} from "./RefundHistory.styled";

const RefundHistory = ({
  payment,
  goToPayment,
  userTimezone,
  refundWindows,
  toggleRefundWindow,
  getDescriptionKeys,
  getSubdescriptionKeys,
  refundsRef,
}) => {
  const obligationSlug = payment?.obligationSlug?.value;
  const descriptionKeys = getDescriptionKeys(obligationSlug);
  const subdescriptionKeys = getSubdescriptionKeys(obligationSlug);
  const refunds = payment.associatedPayments.filter(
    (assoc) => assoc.paymentProcessTypeName.value === "refund"
  );

  const displayLineItemRows = (lineItems, isLastRefund) =>
    lineItems.map((item, i) => {
      const itemID = item?.id?.value;
      const defaultDescription = `Item ${itemID}`;
      const defaultSubDescription = "Line Item";
      const { description, subDescription } = resolveLineItemDescriptions(
        item.customAttributes,
        descriptionKeys,
        subdescriptionKeys
      );
      const amount = item?.amountRefundedInCents?.value ?? 0;
      const isLastLineItem = i == lineItems.length - 1;

      return (
        <LineItemTableRow
          key={item.id.value}
          isLastLineItem={isLastLineItem}
          isLastRefund={isLastRefund}
        >
          <td>
            <Stack childGap="0px">
              <LineItemDescription>
                {description || defaultDescription}
              </LineItemDescription>
              <LineItemSubDescription>
                {subDescription || defaultSubDescription}
              </LineItemSubDescription>
            </Stack>
          </td>
          <td />
          <td />
          <td>
            <LineItemAmount>{displayCurrency(amount)}</LineItemAmount>
          </td>
        </LineItemTableRow>
      );
    });

  const displayRefunds = (refunds) =>
    refunds.map((refund, i) => {
      const refundID = refund?.id?.value ?? "";
      const refundDate = toFormattedDate(refund?.createdAt ?? "", userTimezone);
      const refundStatus = toSentenceCase(refund?.status?.value ?? "");
      const refundAmount = refund?.amount?.value ?? 0;
      const associatedLineItems = refund?.lineItems ?? [];
      const isOpen = refundWindows.has(refundID);
      const isLastRefund = i == refunds.length - 1;
      const lineItemRows = isOpen
        ? displayLineItemRows(associatedLineItems, isLastRefund)
        : [];

      const refundRow = (
        <RefundTableRow
          key={refundID}
          onClick={() => toggleRefundWindow({ refundID })}
          isOpen={isOpen}
          isLastRefund={isLastRefund}
          noHover
        >
          <td>
            <Text extraStyles={"font-size: 14px; line-height: 22px;"}>
              <Link
                to={`/payment/${refundID}`}
                onClick={() => goToPayment({ transactionId: refundID })}
              >
                {refundID}
              </Link>
            </Text>
          </td>
          <td>{refundDate}</td>
          <StatusColumn>{refundStatus}</StatusColumn>
          <td>
            <Cluster justify="flex-end">
              <Text
                weight="600"
                extraStyles={"font-size: 14px; line-height: 22px; text-align: right"}
              >
                {`$${refundAmount}`}
              </Text>
              <Cluster width="20px" extraStyles={"margin-left: 8px;"}>
                {isOpen ? <DownCarrot /> : <RightCarrot />}
              </Cluster>
            </Cluster>
          </td>
        </RefundTableRow>
      );

      return [refundRow, ...lineItemRows];
    });

  return (
    refunds.length > 0 && (
      <Module heading="Refunds" variant="small">
        <Table ref={refundsRef}>
          <TableBody>
            <RefundTableHeaderRow>
              <th>Payment ID</th>
              <th>Date</th>
              <th>Status</th>
              <th>Amount</th>
            </RefundTableHeaderRow>
            {displayRefunds(refunds)}
          </TableBody>
        </Table>
      </Module>
    )
  );
};

export default RefundHistory;
